import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {hasNoValue, isNull, isObject, isPopulatedArray} from './typing.helpers';
import deepEqual from 'fast-deep-equal';

/**
 North American phone number rules:
 - Area codes start with a number 2–9, followed any two digits.  (But there's also a rule that the last two digits can't both be 11, because of 911.)
 - The exchange code starts with a number 2–9 followed by any two digits.
 - The final four digits, known as the station code, have no restrictions.

 For this, the pattern would be something like `/^\(?[2-9]\d{2}\)?[\s-]?[2-9]\d{2}[\s-]?\d{4}$/` -- not counting the "no *11" rule.

 But for now, so as to not introduce a lot of problems with test data, we're simply validating a "reasonably formatted" 10-digit phone number.
 */
export const PHONE_NUMBER_REGEXP: RegExp = /^\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$/;

export const MINIMUM_PASSWORD_LENGTH: number = 8;
export const MAXIMUM_PASSWORD_LENGTH: number = 4096;
export const MINIMUM_PIN_LENGTH: number = 4;
export const MAXIMUM_PIN_LENGTH: number = 16;

export class CommonValidators {
  /** This validator function could be used directly, but it's probably better to use it as a base from which to create other custom validators. */
  public static pattern(pattern: string | RegExp, errorObject: object): ValidatorFn {
    return (control: AbstractControl) => isNull(Validators.pattern(pattern)(control)) ? null : errorObject;
  }

  public static integer(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(/^\d*$/, {mustBeInteger: true})(control);
  }

  public static decimal(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(/^\d*(\.\d+)?$/, {mustBeDecimal: true})(control);
  }

  public static positiveInteger(control: AbstractControl): ValidationErrors | null {
    if (CommonValidators.integer(control) ?? Validators.min(1)(control)) return {mustBePositiveInteger: true};

    return null;
  }

  public static nonNegativeInteger(control: AbstractControl): ValidationErrors | null {
    if (CommonValidators.integer(control) ?? Validators.min(0)(control)) return {mustBeNonNegativeInteger: true};

    return null;
  }

  public static mustBeFalse(control: AbstractControl): ValidationErrors | null {
    return control.value === false ? null : {'mustBeFalse': true};
  }

  public static exactLength(requiredLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const actualLength: number = control.value?.length ?? 0;
      if (actualLength === 0) return null; // Don't validate empty values to allow optional controls (just like Angular's minlength and maxlength validators).

      return actualLength === requiredLength ? null : {exactLength: {requiredLength, actualLength}};
    };
  }

  /* The default value on some required object select fields is often 0, the normal required validator
  considers this valid, this required validator checks for positive integer instead */
  public static requiredIdSelect(control: AbstractControl): ValidationErrors | null {
    if (CommonValidators.integer(control) ?? Validators.min(1)(control)) return {required: true};
    return null;
  }

  public static requiredPopulatedArrayForMultiSelect(control: AbstractControl): ValidationErrors | null {
    if (!isPopulatedArray(control.value)) return {required: true};
    return null;
  }

  /**
   * Returns a "required" error if the control value is not an object, or if the object is deeply equal to the given object.
   * Useful for validating that a selection form field with an object value is not set to a default value, thus forcing the user to make a selection.
   */
  public static objectNotEqualTo(object: object): ValidatorFn {
    return (control: AbstractControl): object | null =>
      !isObject(control.value) || deepEqual(control.value, object) ? {required: true} : null;
  }

  public static readonly pinRegex: RegExp = new RegExp(`^\\d{${MINIMUM_PIN_LENGTH},${MAXIMUM_PIN_LENGTH}}$`);

  public static pinValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.pinRegex, {pinCode: true})(control);
  }

  public static readonly verificationCodeRegex = /^\d{6}$/;

  public static verificationCodeValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.verificationCodeRegex, {verificationCode: true})(control);
  }

  public static readonly zipCodeRegex = /^\d{5}(?:-\d{4})?$/;

  public static zipCodeValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.zipCodeRegex, {zipCode: true})(control);
  }

  public static readonly currencyRegex = /^\d*\.?\d{0,2}$/;

  public static currencyValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.currencyRegex, {currency: true})(control);
  }

  public static currencyMin(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!CommonValidators.currencyValidator(control) && Validators.min(min)(control)) return {currencyMin: {min}};

      return null;
    }
  }

  public static currencyMax(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!CommonValidators.currencyValidator(control) && Validators.max(max)(control)) return {currencyMax: {max}};

      return null;
    }
  }

  // this is very similar to currencyMin, but with different messaging
  public static minPayment(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!CommonValidators.currencyValidator(control) && Validators.min(min)(control)) return {minPayment: {min}};

      return null;
    }
  }

  public static phoneNumber(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null; // Return null if phone input is blank.  This is valid if the field is not required, for instance.

    // There is some value, but some characters may not be numbers.
    const rawValue: string = control.value?.replace(/\D/g, '') ?? ''; // Remove all non-digit characters.
    return rawValue.length === 10
      ? CommonValidators.pattern(PHONE_NUMBER_REGEXP, {mustBeValidPhoneNumber: true})(control)
      : {mustBeTenDigitPhoneNumber: true};
  }

  public static readonly requireDigitRegex = /\d/;

  public static requiresDigitValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.requireDigitRegex, {requiresDigit: true})(control);
  }

  public static readonly requiresUppercaseRegex = /[A-Z]/;

  public static requiresUppercaseValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.requiresUppercaseRegex, {requiresUppercase: true})(control);
  }

  public static readonly requiresLowercaseRegex = /[a-z]/;

  public static requiresLowercaseValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.requiresLowercaseRegex, {requiresLowercase: true})(control);
  }

  public static readonly requiresSpecialCharsRegex = /[^a-zA-Z\d]/;

  public static requiresSpecialCharsValidator(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(CommonValidators.requiresSpecialCharsRegex, {requiresSpecialChars: true})(control);
  }

  public static readonly passwordValidators: ValidatorFn[] = [
    Validators.minLength(MINIMUM_PASSWORD_LENGTH),
    Validators.maxLength(MAXIMUM_PASSWORD_LENGTH),
    CommonValidators.requiresDigitValidator,
    CommonValidators.requiresUppercaseValidator,
    CommonValidators.requiresLowercaseValidator,
    CommonValidators.requiresSpecialCharsValidator
  ];

  public static matchPasswordValidator(passwordControl: AbstractControl | null): ValidatorFn {
    return (confirmPasswordControl: AbstractControl): ValidationErrors | null => {
      if (!passwordControl || !confirmPasswordControl) {
        console.error('Invalid controls in validator'); // eslint-disable-line no-console -- This should never happen, so logging it is good in case it ever does.
        return null;
      }
      const password: string = passwordControl.value;
      const confirmPassword: string = confirmPasswordControl.value;

      return (!!confirmPassword.length) && password !== confirmPassword ? {passwordMismatch: true} : null;
    };
  }

  public static noSpaces(control: AbstractControl): ValidationErrors | null {
    return CommonValidators.pattern(/^\S*$/, {hasInvalidSpace: true})(control);
  }

  public static smsTermsValidator(smsTermsAcceptedControl?: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === 'SMS' && !smsTermsAcceptedControl?.value) {
        return {smsConsentRequired: true};
      }
      return null;
    };
  }

  public static requiresSmsNumberValidator(phoneSmsControl?: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === 'SMS' && hasNoValue(phoneSmsControl?.value)) {
        return {smsSelectedButInvalid: true};
      }
      return null;
    };
  }

  public static requiresPhoneCallNumberValidator(phoneCallControl?: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === 'Phone' && hasNoValue(phoneCallControl?.value)) {
        return {phoneSelectedButInvalid: true};
      }
      return null;
    };
  }

  public static requiresNotificationEmailValidator(emailControl?: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === 'Email' && (!emailControl?.value || emailControl?.invalid)) {
        return {emailSelectedButInvalid: true};
      }
      return null;
    };
  }
}
